import React, { useMemo, useRef } from 'react';
import styled from 'styled-components';

import defaultImg from '../../asset/project/default_1.png';
import {
  convertWorkSizeToString,
  type SelectedData,
  type WorkSimpleForExportData,
} from '../../shared/dto/EntityRepository';
import { usesWorkDuration } from '../../shared/enum/EnumRepository';
import { useOptimizedImageUrl } from '../../shared/hooks/useOptimizedImageUrl';
import { useExportStationWorkStore } from '../../shared/store/exportStationWorkStore';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { useWorkContentModeStore } from '../../shared/store/workContentModeStore';
import { useSettingStore } from '../../shared/store/settingStore';
import { useWorkPropertyVisible } from '../../shared/hooks/useApi/useWorkPropertyVisible';
import { resolveWorkTitle } from '../../shared/util/workDisplayText';
import { resolveWorkArtistName, shouldShowWorkArtist } from '../../shared/util/workArtist';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { WORK_LIST_EXPORT_MAX_ROWS } from '../../shared/constants/limits';
import { AlertPosition, AlertType } from '../../shared/enum/EnumRepository';
import { shrinkString } from '../../shared/util/shrinkString';

import BlockWithVisible from '../projectDetail/BlockWithVisible';

interface SimpleWorkForExportProps {
  data: WorkSimpleForExportData;
  /** 마퀴로 겹친 행(아직 선택 반영 전 프리뷰) */
  isMarqueeHit?: boolean;
}

/** PDF/Excel 선택 모달에서 `addSelectedData`에 넣는 행 단위 페이로드 — 클릭·마퀴 공통 */
export function simpleWorkRowToPdfExportPayload(data: WorkSimpleForExportData): SelectedData {
  return {
    workId: data.id,
    originUrl: data.originUrl,
    title: data.title,
    prodYear: data.prodYear,
    size: data.size,
    sizeText: data.sizeText,
    dimensionsType: data.dimensionsType,
    duration: data.duration,
    artist: data.artist,
  };
}

const SimpleWorkForExport: React.FC<SimpleWorkForExportProps> = ({ data, isMarqueeHit }) => {
  const { addSelectedData, selectedDataList } = useExportStationWorkStore();
  const { enqueueAlert } = useStandardAlertStore();
  const imgContainerRef = useRef<HTMLDivElement>(null);
  const imageUrl = useOptimizedImageUrl(data.originUrl ?? '', imgContainerRef);
  const setting = useSettingStore((s) => s.setting);
  const workContentMode = useWorkContentModeStore((s) => s.workContentMode);
  const { workPropertyVisible } = useWorkPropertyVisible();
  const engVisible = workPropertyVisible?.engVisible !== false;
  const showArtist = shouldShowWorkArtist(setting);
  const displayTitle = useMemo(
    () => resolveWorkTitle({ title: data.title, titleEn: '' }, workContentMode, engVisible),
    [data.title, workContentMode, engVisible],
  );
  const artistName = showArtist
    ? resolveWorkArtistName(data.artist, workContentMode, engVisible)
    : '';

  const isSelected = selectedDataList.some((s) => s.workId === data.id && s.detailId === undefined);

  const handleRowClick = () => {
    if (isSelected) return;
    if (selectedDataList.length >= WORK_LIST_EXPORT_MAX_ROWS) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('WORK_LIST_EXPORT_MAX_ITEMS', {
          maxCount: WORK_LIST_EXPORT_MAX_ROWS,
        }),
      });
      return;
    }
    addSelectedData(simpleWorkRowToPdfExportPayload(data));
  };

  const shrinkedTitle = useMemo(() => shrinkString(displayTitle, 24), [displayTitle]);
  const shrinkedArtist = useMemo(
    () => (artistName ? shrinkString(artistName, 16) : '-'),
    [artistName],
  );
  const sizeString = useMemo(() => {
    if (usesWorkDuration(data.workType)) {
      return data.duration || '-';
    }
    return convertWorkSizeToString(data.size, data.dimensionsType, data.sizeText, 'Variable');
  }, [data]);

  return (
    <>
    <SimpleWorkForExportComp
      key={data.id}
      data-work-id={data.id}
      $isSelected={isSelected}
      $isMarqueeHit={!!isMarqueeHit && !isSelected}
      onClick={handleRowClick}
    >
      <ImageBlock>
        <ImgWrapper ref={imgContainerRef}>
          <WorkImage src={imageUrl === '' ? defaultImg : imageUrl} alt={displayTitle} />
        </ImgWrapper>
      </ImageBlock>
      {showArtist && (
        <ArtistBlock>
          <span>{shrinkedArtist}</span>
        </ArtistBlock>
      )}
      <TitleBlock>
        <span>{shrinkedTitle}</span>
      </TitleBlock>
      <BlockWithVisible
          width={"0.6"}
          value={data.prodYear || '-'}/>
      <BlockWithVisible
          width={"1"}
          value={sizeString}/>
    </SimpleWorkForExportComp>
    </>
  );
};

const SimpleWorkForExportComp = styled.div<{ $isSelected?: boolean; $isMarqueeHit?: boolean }>`
  width: 100%;
  height: fit-content;
  min-height: 50px;
  max-height: 70px;
  position: relative;
  display: flex;
  align-items: center;
  transition: all 0.3s ease-in-out;
  opacity: ${({ $isSelected }) => ($isSelected ? 0.5 : 1)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  box-shadow: ${({ theme, $isMarqueeHit }) =>
    $isMarqueeHit ? `inset 0 0 0 1px ${theme.colors.color_Gray_05}` : 'none'};

  cursor: ${({ $isSelected }) => ($isSelected ? 'not-allowed' : 'pointer')};
`

const ImageBlock = styled.div`
  height: 100%;
  flex: 0.8;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;
const ImgWrapper = styled.div`
  position: relative;
  width: 60%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`
const WorkImage = styled.img`
  //부모 크기에 맞춤
  width: 100%;
  height: 100%; 
  object-fit: contain;
`;

const ArtistBlock = styled.div`
  flex: 1;
  min-width: 0;

  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-left: 4px;
  ${({ theme }) => theme.typography.Body_04};

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const TitleBlock = styled.div`
  flex: 2;
  min-width: 0;

  display: flex;
  align-items: center;
  justify-content: center;
  ${({ theme }) => theme.typography.Body_04};

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

export default SimpleWorkForExport;
